(() => {
  // Prevent duplicate listeners if the snippet loads the asset more than once.
  if (window.atelierBreadcrumbsLoaded) return;
  window.atelierBreadcrumbsLoaded = true;

  class BreadcrumbsManager {
    constructor(wrapper) {
      this.wrapper = wrapper;
      this.schemaScript = wrapper.querySelector('[data-breadcrumb-schema]');
      this.configScript = wrapper.querySelector('[data-breadcrumb-config]');
    }

    init() {
      if (
        this.wrapper.dataset.breadcrumbInitialized ||
        !this.schemaScript ||
        !this.configScript
      ) {
        return;
      }

      try {
        this.config = JSON.parse(this.configScript.textContent);
        this.schema = JSON.parse(this.schemaScript.textContent);
        this.items = this.schema.itemListElement;

        if (!Array.isArray(this.items) || this.items.length < 2) return;

        const isProduct = this.config.pageType === 'product';
        const hasTags = !isProduct && Boolean(this.config.tagsLabel);
        const currentIndex = this.items.length - (hasTags ? 2 : 1);
        const current = this.items[currentIndex];
        const tags = hasTags ? this.items[this.items.length - 1] : null;

        let ancestors = isProduct
          ? this.items.slice(1, -1).filter(
              item => !this.samePath(item.item, this.config.collectionsUrl)
            )
          : [];

        if (isProduct) {
          ancestors = this.getProductTrail(ancestors);
        } else {
          // The current collection is already in JSON-LD.
          // Its visible title remains outside the nav unless tags are active.
          const menuTrail = this.getMenuTrail(current.item);

          if (menuTrail) {
            ancestors = menuTrail.slice(0, -1);
          }
        }

        ancestors = this.cleanTrail(ancestors, current, isProduct);

        const prefix =
          this.config.showCollectionsLink &&
          (!isProduct || ancestors.length)
            ? [
                {
                  name: this.config.collectionsTitle,
                  item: this.config.collectionsUrl
                }
              ]
            : [];

        const trail = [...prefix, ...ancestors];

        const updatedSchema = {
          ...this.schema,
          itemListElement: [
            this.items[0],
            ...trail,
            current,
            ...(tags ? [tags] : [])
          ].map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.item
          }))
        };

        const home = this.wrapper.querySelector('a');
        if (!home) return;

        const content = document.createDocumentFragment();

        content.append(home.cloneNode(true));

        trail.forEach(item => {
          this.appendLink(content, item);
        });

        if (isProduct) {
          // Preserve the original trailing divider.
          // The product title remains outside the nav.
          content.append(this.createDivider());
        } else if (tags) {
          this.appendLink(content, current);
          content.append(this.createDivider());

          const label = document.createElement('span');

          label.setAttribute('aria-current', 'page');
          label.textContent = tags.name;

          content.append(label);
        }

        // Update the existing JSON-LD node instead of adding another one.
        this.schemaScript.textContent = JSON.stringify(updatedSchema).replace(
          /</g,
          '\\u003c'
        );

        content.append(this.schemaScript, this.configScript);

        this.wrapper.replaceChildren(content);
        this.wrapper.dataset.breadcrumbInitialized = 'true';
      } catch (error) {
        // Keep the server-rendered markup and JSON-LD if setup fails.
        console.warn('[BreadcrumbsManager] Error:', error);
      }
    }

    getProductTrail(fallback) {
      // Prefer an explicit /collections/.../products/... context.
      if (this.config.contextCollectionUrl) {
        const trail = this.getMenuTrail(this.config.contextCollectionUrl);

        if (trail) return trail;

        const context = this.config.productCollections.find(collection =>
          this.samePath(
            collection.url,
            this.config.contextCollectionUrl
          )
        );

        if (context) {
          return [
            {
              name: context.title,
              item: this.absoluteUrl(context.url)
            }
          ];
        }
      }

      // Accept a collection referrer only when it belongs to this origin,
      // belongs to the product, and appears in the hierarchy menu.
      const referrer = this.parseUrl(document.referrer);

      if (referrer && referrer.origin === window.location.origin) {
        const source = this.config.productCollections.find(collection =>
          this.samePath(collection.url, referrer.href)
        );

        if (source) {
          const trail = this.getMenuTrail(source.url);

          if (trail) return trail;
        }
      }

      // Multiple metafield collections form an explicit ordered trail.
      // Expand a single fallback collection using the menu hierarchy.
      if (fallback.length === 1) {
        return this.getMenuTrail(fallback[0].item) || fallback;
      }

      return fallback;
    }

    getMenuTrail(url) {
      const target = this.pathKey(url);
      if (!target) return null;

      let match = null;

      const visit = (links, parents = []) => {
        for (const link of links || []) {
          const trail = [...parents, link];

          if (
            this.pathKey(link.url) === target &&
            (!match || trail.length > match.length)
          ) {
            match = trail;
          }

          visit(link.children, trail);
        }
      };

      visit(this.config.menu);

      if (!match) return null;

      // Omit the top level only when it is an ancestor of another item.
      if (this.config.ignoreLevel1 && match.length > 1) {
        match = match.slice(1);
      }

      return match
        .filter(link => this.pathKey(link.url))
        .map(link => ({
          name: link.title,
          item: this.absoluteUrl(link.url)
        }));
    }

    cleanTrail(trail, current, isProduct) {
      const seen = new Set([
        this.pathKey(this.items[0].item)
      ]);

      if (this.config.showCollectionsLink) {
        seen.add(this.pathKey(this.config.collectionsUrl));
      }

      if (!isProduct) {
        seen.add(this.pathKey(current.item));
      }

      return trail.filter(item => {
        const key = this.pathKey(item.item);

        if (!key || seen.has(key)) return false;

        seen.add(key);
        return true;
      });
    }

    parseUrl(value) {
      if (
        typeof value !== 'string' ||
        !value.trim() ||
        value.trim().startsWith('#')
      ) {
        return null;
      }

      try {
        const url = new URL(value, window.location.origin);

        return ['http:', 'https:'].includes(url.protocol)
          ? url
          : null;
      } catch {
        return null;
      }
    }

    pathKey(value) {
      const url = this.parseUrl(value);

      if (!url || url.origin !== window.location.origin) {
        return null;
      }

      return url.pathname.replace(/\/+$/, '') || '/';
    }

    samePath(first, second) {
      const key = this.pathKey(first);

      return key !== null && key === this.pathKey(second);
    }

    absoluteUrl(value) {
      return this.parseUrl(value)?.href;
    }

    createDivider() {
      const divider = document.createElement('span');

      divider.className = 'breadcrumb__divider';
      divider.setAttribute('aria-hidden', 'true');
      divider.textContent = '/';

      return divider;
    }

    appendLink(container, item) {
      container.append(this.createDivider());

      const link = document.createElement('a');

      link.href = item.item;
      link.textContent = item.name;

      container.append(link);
    }
  }

  const initialize = (root = document) => {
    root.querySelectorAll('[data-breadcrumb]').forEach(wrapper => {
      new BreadcrumbsManager(wrapper).init();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initialize(),
      { once: true }
    );
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', event => {
    initialize(event.target);
  });
})();